import React, { useCallback } from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';

import {
  Form,
  minValue,
  NumberInput,
  RecordContextProvider,
  required,
  SelectInput,
  useListContext,
  useNotify,
  useRecordContext,
  useRefresh,
  useUnselectAll,
  useUpdateMany,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import SelectedPingsList from './SelectedPingsList';
import pingStatuses, { managePingStatusChoices } from '../../../constants/pingStatuses';
import { recommendationsChoices } from '../../../constants/recommendations';
import StatusField from '../../../customFields/StatusField';
import ColoredScoreField from '../../../customFields/ColoredScoreField';
import RadioInput from '../../../customFields/RadioInput';
import CommentInput from '../../comment/CommentInput';
import DraggableDialog from '../../layout/dialog/DraggableDialog';
import { Ping } from '../../../utilities/schemas/ping';

export type HandlePingForm = {
  status: string;
  comment: string;
  overrideRecommendation?: string;
  numberOfDaysIgnoreRule?: number;
  isBinaryRule: boolean;
};

export const UpdatePingDialog = ({
  closeDialog,
  variant,
  selectedIds = [],
}: {
  closeDialog: () => void;
  variant: 'bulk' | 'single';
  selectedIds?: string[];
}) => {
  const statusValue = useWatch({ name: 'status' });

  return (
    <>
      <DialogContent>
        {variant === 'bulk' && (
          <Box mb={3}>
            <SelectedPingsList />
          </Box>
        )}
      </DialogContent>
      <DialogContent>
        <Grid container spacing={6}>
          <Grid size={12}>
            <Grid container spacing={2}>
              {variant === 'single' && (
                <Grid marginBottom={2} size={12}>
                  <Typography variant="label">Current status</Typography>
                  <StatusField source="status" />
                </Grid>
              )}
              <Grid size={12}>
                <Typography variant="label">
                  What should the Ping status be updated to?
                </Typography>
              </Grid>
              <Grid size={12}>
                <RadioInput
                  source="status"
                  validate={required()}
                  choices={managePingStatusChoices}
                  row
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Grid container spacing={1}>
              <Grid size={12}>
                <Typography variant="label">
                  {variant === 'bulk' && 'Comment on the reason for why you want to bulk update the pings statuses'}
                  {variant === 'single' && 'Comment on the reason for why you want to update the ping status'}
                </Typography>
              </Grid>
              <Grid size={12}>
                <CommentInput source="comment" />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="label">
                  What should be the recommendation for future
                  requests with the same underlying rule?
                </Typography>
              </Grid>
              <Grid size={12}>
                <SelectInput
                  defaultValue="proceed"
                  label="Future recommendation"
                  choices={recommendationsChoices}
                  fullWidth
                  source="overrideRecommendation"
                  validate={required()}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="label">
                  For how long should the system use this decision for future requests?
                  (During that time pings won&apos;t be created for that rule(s) and
                  recommendation will be inferred from the decision)
                </Typography>
              </Grid>
              <Grid size={12}>
                <NumberInput fullWidth source="numberOfDaysIgnoreRule" validate={[minValue(0), required()]} />
              </Grid>
            </Grid>
          </Grid>
          {variant === 'single' && statusValue === pingStatuses.CONFIRMED && (
            <Grid size={12}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Box display="flex" flexDirection="column">
                    <Typography variant="label">Score</Typography>
                    <ColoredScoreField source="score" />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} color="error">
          Cancel
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={variant === 'bulk' && selectedIds.length === 0}
        >
          {variant === 'bulk' ? `Update ${selectedIds.length} pings` : 'Save'}
        </Button>
      </DialogActions>
    </>
  );
};

const handleId = 'bulk-update-ping-status-dialog-title';

const BulkUpdateDialog = ({
  open,
  closeDialog,
  resource = 'cases',
}: {
  open: boolean;
  closeDialog: () => void;
  resource?: string;
}) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const [update] = useUpdateMany();
  const unselectAll = useUnselectAll(resource);
  const record = useRecordContext<Ping>();

  const { selectedIds } = useListContext();

  const onSubmit = useCallback((data: Partial<HandlePingForm>) => {
    update(
      'pings',
      {
        ids: selectedIds.map((id) => `${id}/status`),
        data,
      },
      {
        onSuccess: () => {
          refresh();
          closeDialog();
          unselectAll();
        },
        onError: () => {
          notify('Could not update ping status', { type: 'error' });
        },
      },
    );
  }, [closeDialog, notify, refresh, selectedIds, unselectAll, update]);

  return (
    <DraggableDialog
      open={open}
      onClose={closeDialog}
      fullWidth
      maxWidth="md"
      handleId={handleId}
    >
      <DialogTitle variant="h4" id={handleId}>
        Bulk manage the status of the Pings
      </DialogTitle>
      <Box display="block">
        <Form mode="all" record={{ numberOfDaysIgnoreRule: 0 }} onSubmit={onSubmit} sanitizeEmptyValues>
          <RecordContextProvider value={record}>
            <UpdatePingDialog
              closeDialog={closeDialog}
              variant="bulk"
              selectedIds={selectedIds}
            />
          </RecordContextProvider>
        </Form>
      </Box>
    </DraggableDialog>
  );
};

export default BulkUpdateDialog;
